import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Phone, PhoneIncoming, PhoneOutgoing, Voicemail, MessageSquare, Bot, X, RefreshCw } from 'lucide-react';
import { theme, cardStyle } from '../theme';

// T1.10 — Aggregate every conversation with this patient: CallLogs +
// FrontDeskThreads. Sorted by recency. Includes a Click-to-Call button.

export default function PatientCommsPane({ patientId, patientPhone, API_BASE }) {
  const [calls, setCalls]     = useState([]);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDial, setShowDial] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const phone10 = String(patientPhone || '').replace(/\D/g, '').slice(-10);
      const [c, t] = await Promise.all([
        axios.get(`${API_BASE}/calls`, { params: { patientId, limit: 100 } }).catch(() => ({ data: [] })),
        phone10 ? axios.get(`${API_BASE}/frontdesk/threads`, { params: { phone: phone10, limit: 50 } }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setCalls(c.data || []);
      setThreads(t.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API_BASE, patientId, patientPhone]);

  useEffect(() => { load(); }, [load]);

  // Merge into a single timeline
  const items = [
    ...calls.map(c => ({
      kind: c.outcome === 'voicemail' ? 'voicemail' : 'call',
      at: c.createdAt, raw: c,
    })),
    ...threads.map(t => ({ kind: 'thread', at: t.updatedAt, raw: t })),
  ].sort((a, b) => new Date(b.at) - new Date(a.at));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: theme.textMuted }}>
          {calls.length} calls · {threads.length} message threads
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {patientPhone && <button onClick={() => setShowDial(true)} style={btn(theme.primary)}><Phone size={13}/> Click-to-Call</button>}
          <button onClick={load} style={btn(theme.secondary)}><RefreshCw size={13}/> Refresh</button>
        </div>
      </div>

      {loading ? <div style={{ padding: 20, color: theme.textMuted }}>Loading…</div> : items.length === 0 ? (
        <div style={{ padding: 30, textAlign: 'center', color: theme.textMuted }}>No communications yet for this patient.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((it, i) => <CommItem key={i} item={it}/>)}
        </div>
      )}

      {showDial && <DialModal API_BASE={API_BASE} patientId={patientId} patientPhone={patientPhone} onClose={()=>setShowDial(false)} onSent={()=>{ setShowDial(false); load(); }}/>}
    </div>
  );
}

function CommItem({ item }) {
  const c = item.raw;
  if (item.kind === 'voicemail') {
    return (
      <div style={row(theme.warning)}>
        <Voicemail size={16} color={theme.warning}/>
        <div style={{ flex: 1 }}>
          <strong style={{ fontSize: 13 }}>Voicemail</strong>
          <div style={{ fontSize: 11.5, color: theme.textMuted }}>📞 {c.fromNumber} · {Math.round((c.durationSec||0)/60)}m {((c.durationSec||0)%60)}s</div>
          {c.recordingUrl && <audio controls src={c.recordingUrl} style={{ marginTop: 6, height: 28, width: '100%' }}/>}
        </div>
        <small style={{ color: theme.textMuted }}>{when(item.at)}</small>
      </div>
    );
  }
  if (item.kind === 'call') {
    return (
      <div style={row(c.direction === 'inbound' ? theme.info : theme.success)}>
        {c.direction === 'inbound' ? <PhoneIncoming size={16} color={theme.info}/> : <PhoneOutgoing size={16} color={theme.success}/>}
        <div style={{ flex: 1 }}>
          <strong style={{ fontSize: 13 }}>{c.direction === 'inbound' ? 'Inbound' : 'Outbound'} call · {c.outcome}</strong>
          <div style={{ fontSize: 11.5, color: theme.textMuted }}>
            {c.fromNumber} → {c.toNumber} · {Math.round((c.durationSec||0)/60)}m
            {c.sentiment !== 'unknown' && ` · ${c.sentiment}`}
            {c.missedBookingFlag && ' · ⚠️ missed booking'}
          </div>
          {c.summary && <div style={{ fontSize: 12, marginTop: 4, fontStyle: 'italic' }}>{c.summary}</div>}
        </div>
        <small style={{ color: theme.textMuted }}>{when(item.at)}</small>
      </div>
    );
  }
  // thread
  const last = c.messages?.[c.messages.length - 1];
  return (
    <div style={row(c.status === 'escalated' ? theme.danger : theme.purple)}>
      <MessageSquare size={16} color={theme.purple}/>
      <div style={{ flex: 1 }}>
        <strong style={{ fontSize: 13 }}>WhatsApp · {c.intent || 'thread'}</strong>
        {c.status === 'escalated' && <span style={{ marginLeft: 6, fontSize: 10, padding: '2px 6px', background: theme.danger + '20', color: theme.danger, borderRadius: 8, fontWeight: 700 }}>ESCALATED</span>}
        <div style={{ fontSize: 11.5, color: theme.textMuted }}>{c.messages?.length || 0} messages · {c.bookingCreated && '✅ booking created'}</div>
        {last && <div style={{ fontSize: 12, marginTop: 4, color: theme.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <strong>{last.role === 'patient' ? 'Patient' : last.role === 'ai' ? 'AI' : 'Staff'}:</strong> {last.text}
        </div>}
      </div>
      <small style={{ color: theme.textMuted }}>{when(item.at)}</small>
    </div>
  );
}

function DialModal({ API_BASE, patientId, patientPhone, onClose, onSent }) {
  const [from, setFrom] = useState('');
  const [busy, setBusy] = useState(false);
  const dial = async () => {
    if (!from) return alert('Enter your number');
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/voip/click-to-call`, { fromNumber: from, toNumber: patientPhone, patientId });
      alert(r.data.provider === 'stub' ? '⚠️ Stub mode — call not actually placed.' : `✅ Call initiated`);
      onSent();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    setBusy(false);
  };
  return (
    <div style={overlay}>
      <div style={{ ...cardStyle, padding: 20, width: 'min(420px, 92vw)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>📞 Call {patientPhone}</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}><X/></button>
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 12, color: theme.textMuted, fontWeight: 700 }}>Your number (will ring first)</label>
          <input value={from} onChange={e=>setFrom(e.target.value)} style={{ width: '100%', padding: 8, border: `1px solid ${theme.border}`, borderRadius: 6, marginTop: 4 }} placeholder="+91 98XXXXXXXX"/>
          <button onClick={dial} disabled={busy} style={{ ...btn(theme.primary), width: '100%', marginTop: 12 }}>{busy ? 'Bridging…' : 'Place Call'}</button>
        </div>
      </div>
    </div>
  );
}

function when(d) { try { return new Date(d).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }); } catch { return ''; } }

const row = (color) => ({
  display: 'flex', alignItems: 'flex-start', gap: 12, padding: 12,
  background: '#fff', border: `1px solid ${theme.borderLight}`,
  borderLeft: `4px solid ${color}`, borderRadius: theme.radiusSm,
});
const btn = (c) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: c, color: '#fff', border: 'none', borderRadius: theme.radiusSm, cursor: 'pointer', fontWeight: 600, fontSize: 13 });
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
